/**
 * How many messages are waiting, shared by the two things that show it.
 *
 * Gordon asked for the count on the Chat menu item as well as in the bell. The
 * bell already polls /api/notifications every half minute, and that response
 * now carries `chat_unread` - so a second poll just to paint a badge would be
 * two requests where one will do, and two numbers that can disagree with each
 * other on screen.
 *
 * So this is a two-line store rather than a fetch: the bell writes what it
 * learned, the badge reads it. Deliberately not React context - context would
 * mean a provider wrapped around the navbar and a re-render of everything
 * inside it on every poll, to move one integer.
 *
 * It is NOT persisted anywhere. A reload shows nothing until the first poll
 * comes back a moment later, which is the honest state: we do not know yet.
 * A number remembered from last time would be a number that is wrong.
 */

let count = 0;
const listeners = new Set();

/** The current count. Zero until the first poll answers. */
export const unreadCount = () => count;

/** Called by whoever learns the number. Notifies only on a real change. */
export function setUnreadCount(next) {
  const n = Number.isFinite(Number(next)) ? Math.max(0, Math.floor(Number(next))) : 0;
  if (n === count) return count;
  count = n;
  for (const fn of listeners) {
    // One bad listener must not stop the others being told.
    try { fn(count); } catch { /* ignore */ }
  }
  return count;
}

/** Subscribe. Returns the unsubscribe, which callers must actually call. */
export function onUnreadCount(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Signed out, or switched account: forget it.
 *
 * Without this, a badge left over from the previous session sits above a chat
 * screen that is empty - which looks like a bug in the chat screen rather than
 * a stale number.
 */
export const clearUnreadCount = () => setUnreadCount(0);
